import { useState, useEffect } from 'react';
import { Badge } from '../../ui/components';
import { BookOpen, ChevronRight, X, Video, FileText } from 'lucide-react';
import { LK_FEATURES } from '../../config/features';

const educationContent = {
    'assessment': {
      label: 'Assessment',
      title: 'How recommendations work',
      body: 'Answer a few questions so we can match you to loan products that fit your goals and qualify profile.',
      bullets: [
        'We are optimizing for approval + best terms, not just lowest rate.',
        'Be honest - underwriting will validate income, assets, and credit later.',
        'You can save and return anytime.',
      ],
      resources: [
        { label: 'Conventional vs FHA', href: '#' },
        { label: 'DTI explained', href: '#' },
      ],
    },
    'application': {
      label: 'Application',
      title: 'What lenders verify',
      body: 'This is where you provide the details that will later be verified with documents and third-party sources.',
      bullets: [
        'Employment and income stability matter as much as total income.',
        'Try to keep bank activity predictable (avoid large unexplained deposits).',
        'If you have multiple jobs, list them all - consistency helps.',
      ],
      resources: [
        { label: 'Income types (W2, 1099, self-employed)', href: '#' },
        { label: 'Assets and reserves', href: '#' },
      ],
    },
    'documents': {
      label: 'Documents',
      title: 'Documents that speed up approval',
      body: 'A clean document set is the fastest way to reduce conditions and avoid back-and-forth.',
      bullets: [
        'Upload full statements (all pages), even if blank pages exist.',
        'Use PDFs when possible - photos slow review and can be unreadable.',
        'Name files clearly (e.g., BankStatement_ABC_2025-11.pdf).',
      ],
      resources: [
        { label: 'Document checklist', href: '#' },
        { label: 'How to avoid common conditions', href: '#' },
      ],
    },
    'underwriting': {
      label: 'Underwriting',
      title: 'Underwriting basics',
      body: 'Underwriting is the risk review. The goal is to confirm the file meets guidelines before submission and closing.',
      bullets: [
        'Conditions are normal - they are requests to verify specific items.',
        'Respond quickly and keep explanations short and factual.',
        'Avoid new credit inquiries during the process.',
      ],
      resources: [
        { label: 'What is underwriting?', href: '#' },
        { label: 'Conditions explained', href: '#' },
      ],
    },
    'ai-underwriting': {
      label: 'AI Underwriting',
      title: 'What AI can catch early',
      body: 'AI review is great for surfacing issues early so you can fix them before a lender sees the file.',
      bullets: [
        'Spotting gaps in documentation and missing signatures.',
        'Highlighting DTI/asset pressure points.',
        'Recommending quick fixes to improve readiness.',
      ],
      resources: [{ label: 'Improving readiness score', href: '#' }],
    },
    'human-underwriting': {
      label: 'Human Underwriting',
      title: 'How a human underwriter helps',
      body: 'A human underwriter can interpret nuance and help you position the file to fit guidelines.',
      bullets: [
        'Great when income is complex or documentation is non-standard.',
        'You will get clear guidance on what to upload and why.',
        'Expect fewer surprises later.',
      ],
      resources: [{ label: 'When to choose human underwriting', href: '#' }],
    },
    'forward': {
      label: 'Share',
      title: 'Sending your file to a loan officer',
      body: 'When you forward your packet, your loan officer or realtor sees a clean summary of your profile, the recommended program, and Owen\'s readiness assessment.',
      bullets: [
        'A complete file means fewer surprises and faster turnaround.',
        'You stay in control of who gets your details.',
        'Bring the packet to any lender - your file is portable.',
      ],
      resources: [{ label: 'How to compare loan estimates', href: '#' }],
    },
    'submit': {
      label: 'Submit',
      title: 'What happens after you finish',
      body: 'Once Owen completes the review, you have a pre-underwritten file you can share with your lender of choice.',
      bullets: [
        'Bring it to your loan officer to start the formal application.',
        'Keep funds stable - avoid large transfers unless necessary.',
        'Locking a rate is a strategic decision; ask your LO about timing.',
      ],
      resources: [{ label: 'Rate locks 101', href: '#' }],
    },
    'general': {
      label: 'Knowledge',
      title: 'Mortgage education center',
      body: 'Learn about the mortgage process, requirements, and best practices.',
      bullets: [
        'Understanding loan types and requirements',
        'Document preparation and organization',
        'Timeline and process expectations',
      ],
      resources: [
        { label: 'Mortgage basics', href: '#' },
        { label: 'First-time buyer guide', href: '#' },
      ],
    },
  };

const FloatingKnowledge = ({ 
  context = 'general',
  step = null,
  customContent = null,
}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [isMinimized, setIsMinimized] = useState(true);
  const [content, setContent] = useState(null);

  useEffect(() => {
    if (customContent) {
      setContent(customContent);
    } else {
      setContent(educationContent[context] || educationContent.general);
    }
  }, [context, customContent]);

  if (!LK_FEATURES.knowledgeCenter) return null;

  if (isMinimized) {
    return (
      <div
        style={{
          position: 'fixed',
          right: 0,
          top: '35%',
          transform: 'translateY(-50%)',
          zIndex: 1039,
          cursor: 'pointer',
        }}
        onClick={() => {
          setIsMinimized(false);
          setIsExpanded(true);
        }}
      >
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 'var(--lk-space-2)',
            padding: 'var(--lk-space-3) var(--lk-space-3)',
            background: 'var(--lk-primary-400)',
            color: 'white',
            borderTopLeftRadius: 'var(--lk-radius-md)',
            borderBottomLeftRadius: 'var(--lk-radius-md)',
            boxShadow: '-2px 0 8px rgba(0,0,0,0.1)',
            writingMode: 'vertical-rl',
            textOrientation: 'mixed',
          }}
        >
          <BookOpen size={20} />
          <span style={{ fontWeight: 600, fontSize: 'var(--lk-text-sm)' }}>Knowledge</span>
        </div>
      </div>
    );
  }

  if (!content) return null;

  return (
    <div
      style={{
        position: 'fixed',
        right: isExpanded ? 0 : -340,
        top: 0,
        height: '100vh',
        width: isExpanded ? '380px' : '60px',
        transition: 'all 0.3s ease',
        zIndex: 1039,
        display: 'flex',
        flexDirection: 'row',
      }}
    >
      <div
        style={{
          width: '40px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: 'var(--lk-primary-400)',
          color: 'white',
          cursor: 'pointer',
          borderTopLeftRadius: isExpanded ? 0 : 'var(--lk-radius-md)',
          borderBottomLeftRadius: isExpanded ? 0 : 'var(--lk-radius-md)',
          boxShadow: '-2px 0 8px rgba(0,0,0,0.1)',
        }}
        onClick={() => {
          if (isExpanded) {
            setIsExpanded(false);
            setIsMinimized(true);
          } else {
            setIsExpanded(true);
          }
        }}
      >
        {isExpanded ? (
          <ChevronRight size={24} />
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 'var(--lk-space-2)', padding: 'var(--lk-space-4) 0' }}>
            <BookOpen size={24} />
            <div style={{ writingMode: 'vertical-rl', textOrientation: 'mixed' }}>
              <span style={{ fontWeight: 600, fontSize: 'var(--lk-text-sm)' }}>Learn</span>
            </div>
          </div>
        )}
      </div>

      <div
        style={{
          width: '340px',
          height: '100vh',
          display: 'flex',
          flexDirection: 'column',
          boxShadow: '-4px 0 12px rgba(0,0,0,0.1)',
          overflowY: 'auto',
          background: 'white',
          borderLeft: '1px solid var(--lk-border-subtle)',
        }}
      >
        <div style={{ background: 'var(--lk-primary-400)', color: 'white', padding: 'var(--lk-space-3)', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--lk-space-2)' }}>
            <BookOpen size={24} />
            <div>
              <div style={{ fontWeight: 700 }}>Knowledge Center</div>
              <div style={{ fontSize: '0.7rem', opacity: 0.9 }}>Context-aware education</div>
            </div>
          </div>
          <button
            style={{ background: 'none', border: 'none', color: 'white', cursor: 'pointer', padding: 4 }}
            onClick={() => { setIsExpanded(false); setIsMinimized(true); }}
          >
            <X size={18} />
          </button>
        </div>

        <div style={{ padding: 'var(--lk-space-3)' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 'var(--lk-space-2)', marginBottom: 'var(--lk-space-3)' }}>
            <h6 style={{ fontWeight: 700, margin: 0 }}>Education</h6>
            <Badge variant="primary">{content.label}</Badge>
          </div>

          <h6 style={{ fontWeight: 600, marginBottom: 'var(--lk-space-2)' }}>{content.title}</h6>
          <p style={{ color: 'var(--lk-text-tertiary)', fontSize: 'var(--lk-text-sm)', marginBottom: 'var(--lk-space-3)' }}>{content.body}</p>

          <div
            style={{ aspectRatio: '16 / 9', border: '1px solid var(--lk-border-subtle)', borderRadius: 'var(--lk-radius-md)', marginBottom: 'var(--lk-space-3)', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'var(--lk-gray-50)' }}
          >
            <div style={{ textAlign: 'center', color: 'var(--lk-text-quaternary)' }}>
              <Video size={32} style={{ marginBottom: 'var(--lk-space-2)' }} />
              <div style={{ fontSize: 'var(--lk-text-sm)' }}>Video placeholder (16:9)</div>
            </div>
          </div>

          <hr style={{ border: 'none', borderTop: '1px solid var(--lk-border-subtle)', margin: 'var(--lk-space-3) 0' }} />

          <div style={{ marginBottom: 'var(--lk-space-3)' }}>
            <div style={{ color: 'var(--lk-text-tertiary)', fontSize: 'var(--lk-text-sm)', fontWeight: 600, marginBottom: 'var(--lk-space-2)' }}>Key takeaways</div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--lk-space-2)' }}>
              {content.bullets.map((bullet, idx) => (
                <div key={idx} style={{ display: 'flex', gap: 'var(--lk-space-2)' }}>
                  <span style={{ color: 'var(--lk-primary-500)' }}>•</span>
                  <span style={{ fontSize: 'var(--lk-text-sm)', color: 'var(--lk-text-tertiary)' }}>{bullet}</span>
                </div>
              ))}
            </div>
          </div>

          <div>
            <div style={{ color: 'var(--lk-text-tertiary)', fontSize: 'var(--lk-text-sm)', fontWeight: 600, marginBottom: 'var(--lk-space-2)', display: 'flex', alignItems: 'center', gap: 4 }}>
              <FileText size={14} />
              Learn more
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--lk-space-2)' }}>
              {content.resources.map((resource, idx) => (
                <a key={idx} href={resource.href} style={{ fontSize: 'var(--lk-text-sm)', textDecoration: 'none', color: 'var(--lk-primary-500)' }}>
                  {resource.label} →
                </a>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FloatingKnowledge;
